import React, { useState, useEffect } from "react";
import "../../../css/CRUDModal.css";
import { apiList } from "../../../helpers/apiList";
import { axiosPostApi } from "../../../helpers/interceptorServices";
import HeaderWithBreadcrumb from "../../../components/common/HeaderWithBreadcrumb";
import MasterTable from "../../../components/common/MasterTable";
import Loader from "../../../components/Loader/Loader";
import AddRateCardModal from "./AddRateCardModal";
import RateCardFilterModal from "./RateCardFilterModal";
import { format, parse } from "date-fns";
import FilterBar from "../../../components/common/FilterBar";

const RATE_CARD_STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "In-Active" },
];

const defaultFilterData = {
  rateCardId: "",
  rateCardName: "",
  status: "",
  search: "",
};

// `embedded`: shown inside another screen (e.g. the Rate Cards pop-up on Charges Master)
const RateCardMaster = ({ embedded = false }) => {
  const [pageSize, setPageSize] = useState(10);
  const loggedInUserName = sessionStorage.getItem("username");
  const [loading, setLoading] = useState(true);
  const [noData, setNoData] = useState(false);
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageDetails, setPageDetails] = useState(null);
  const [editData, setEditData] = useState({});
  const [modalAction, setModalAction] = useState("");
  const [columnData, setColumnData] = useState("");
  const [isModalOpenCreate, setIsModalOpenCreate] = useState(false);
  const [isModalOpenFilter, setIsModalOpenFilter] = useState(false);
  const [filterData, setFilterData] = useState(defaultFilterData);
  const [rawMasterData, setRawMasterData] = useState([]);

  // ✅ Modal open/close
  const openModalCreate = () => setIsModalOpenCreate(true);
  const closeModalCreate = () => setIsModalOpenCreate(false);

  const openModalFilter = () => setIsModalOpenFilter(true);
  const closeModalFilter = () => setIsModalOpenFilter(false);

  // ✅ Table headers
  const chargeMasterHeader = [
    "Rate Card Id",
    "Rate Card Name",
    "Created Date",
    "Status",
  ];

  const chargeMasterHeaderToKeyMap = {
    "Rate Card Id": "rateCardId",
    "Rate Card Name": "rateCardName",
    "Created Date": "createdDt",
    Status: "status",
  };

  const modifyStructure = (response) => {
    return response?.map((item, index) => {
      let formattedCreDate = item.createdDt;

      let parsedDate = formattedCreDate
        ? parse(formattedCreDate, "dd-MM-yyyy HH:mm:ss", new Date())
        : null;

      return {
        ...item,
        id: item.id || index + 1,
        createdDt: parsedDate
          ? [
              format(parsedDate, "dd-MM-yyyy"),
              <br key={index} />,
              format(parsedDate, "HH:mm:ss"),
            ]
          : item.createdDt || "",
      };
    });
  };

  // ✅ Fetch data (with dummy fallback & filtering)
  const fetchChargeMasterData = async (
    page = currentPage,
    size = pageSize,
    currentFilters = filterData
  ) => {
    const hasFilters = Boolean(
      (currentFilters?.rateCardId &&
        currentFilters.rateCardId.trim() !== "") ||
        (currentFilters?.rateCardName &&
          currentFilters.rateCardName.trim() !== "") ||
        (currentFilters?.status && currentFilters.status.trim() !== "") ||
        (currentFilters?.search && currentFilters.search.trim() !== "")
    );

    const isPartialRateCardId =
      currentFilters?.rateCardId &&
      !/^RC\d{3,}$/i.test(currentFilters.rateCardId.trim());

    const payload = {
      username: loggedInUserName,
      ...(!isPartialRateCardId &&
        currentFilters?.rateCardId && {
          rateCardId: currentFilters.rateCardId.trim(),
        }),
      ...(currentFilters?.rateCardName && {
        rateCardName: currentFilters.rateCardName.trim(),
      }),
      ...(currentFilters?.status && {
        status: currentFilters.status.trim(),
      }),
    };

    setLoading(true);

    try {
      const queryPageSize = hasFilters ? 1000 : size;
      const queryPageNo = hasFilters ? 1 : page;

      const response = await axiosPostApi(
        `${apiList.FETCH_RATE_CARD_MASTER}?&pageSize=${queryPageSize}&pageNo=${queryPageNo}`,
        payload
      );

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        let modifiedData = modifyStructure(response?.data?.data) || [];
        setRawMasterData(modifiedData);

        if (hasFilters) {
          if (
            currentFilters?.rateCardId &&
            currentFilters.rateCardId.trim() !== ""
          ) {
            const targetId = currentFilters.rateCardId.trim().toLowerCase();
            modifiedData = modifiedData.filter((item) =>
              (item?.rateCardId || "").trim().toLowerCase().includes(targetId)
            );
          }

          if (
            currentFilters?.rateCardName &&
            currentFilters.rateCardName.trim() !== ""
          ) {
            const targetName = currentFilters.rateCardName.trim().toLowerCase();
            modifiedData = modifiedData.filter((item) =>
              (item?.rateCardName || "").trim().toLowerCase().includes(targetName)
            );
          }

          if (currentFilters?.status && currentFilters.status.trim() !== "") {
            const targetStatus = currentFilters.status.trim().toLowerCase();
            modifiedData = modifiedData.filter(
              (item) =>
                (item?.status || "").trim().toLowerCase() === targetStatus
            );
          }

          // search box: rate card ID or name (filtered in the browser)
          if (currentFilters?.search && currentFilters.search.trim() !== "") {
            const term = currentFilters.search.trim().toLowerCase();
            modifiedData = modifiedData.filter((item) =>
              [item?.rateCardId, item?.rateCardName].some((v) =>
                String(v ?? "").toLowerCase().includes(term),
              ),
            );
          }

          const totalCount = modifiedData.length;
          const totalPages = Math.ceil(totalCount / size) || 1;
          const startIndex = (page - 1) * size;
          const paginatedData = modifiedData.slice(
            startIndex,
            startIndex + size
          );

          // same shape as the server's pageDetails (the table reads pageNo / noOfPages)
          setPageDetails({
            pageNo: page,
            currentPage: page,
            pageSize: size,
            totalRecords: totalCount,
            noOfPages: totalPages,
            totalPages: totalPages,
          });
          setData(paginatedData);
          setNoData(paginatedData.length === 0);
        } else {
          setPageDetails(response?.data?.pageDetails);
          setData(modifiedData);
          setNoData(modifiedData?.length === 0);
        }
      } else {
        setData([]);
        setNoData(true);
      }
    } catch (error) {
      console.warn("⚠️ API failed - showing dummy data for UI testing");
      setData([]);
      setNoData(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChargeMasterData(1, pageSize, defaultFilterData);
  }, []);

  // ✅ Modal actions (add, modify, view)
  const handleChargeMaster = (data, actionType = "view") => {
    if (!["add", "modify", "view"].includes(actionType)) return;

    setModalAction(actionType);
    setColumnData(data);

    if (actionType === "add") {
      setEditData({});
      openModalCreate();
    } else if (actionType === "modify") {
      setEditData(data);
      openModalCreate();
    } else if (actionType === "view") {
      openModalCreate();
      setEditData(data);
    }
  };

  const applyFilters = (newFilters = filterData) => {
    setFilterData(newFilters);
    setCurrentPage(1);
    fetchChargeMasterData(1, pageSize, newFilters);
  };

  const handleReset = () => {
    setFilterData(defaultFilterData);
    setCurrentPage(1);
    fetchChargeMasterData(1, pageSize, defaultFilterData);
  };

  const totalRecord = pageDetails?.totalRecords || data.length;

  return (
    <>
      {!embedded && <HeaderWithBreadcrumb title="Rate Card Master" breadcrumb="Master" />}
      <FilterBar
        values={filterData}
        onChange={(next) => {
          setFilterData(next);
          setCurrentPage(1);
          fetchChargeMasterData(1, pageSize, next);
        }}
        search={{ key: "search", placeholder: "Search rate card ID or name" }}
        selects={[{ key: "status", allLabel: "All statuses", chipLabel: "Status", options: RATE_CARD_STATUS_OPTIONS }]}
      />
      <div>
        <div className="bg-[#FFFFFF] shadow-custom rounded-[14px] px-[10px] py-[10px]">
          {loading ? (
            <div className="flex justify-center items-center py-10">
              <Loader />
            </div>
          ) : (
            <MasterTable
              loading={loading}
              headers={chargeMasterHeader}
              data={data}
              filterFields={["rateCardId", "rateCardName", "status"]}
              entityType="RateCardMaster"
              headerToKeyMap={chargeMasterHeaderToKeyMap}
              handleAction={handleChargeMaster}
              noData={noData}
              pageDetails={pageDetails}
              currentPage={currentPage}
              pageSize={pageSize}
              totalRecord={totalRecord}
              fetchNextData={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
                fetchChargeMasterData(page, size, filterData);
              }}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              openModalFilter={openModalFilter}
              handleReset={handleReset}
              filterData={filterData}
              fetchData={() => {}}
              hideFilterControls
            />
          )}

          {isModalOpenCreate && (
            <AddRateCardModal
              show={isModalOpenCreate}
              handleClose={closeModalCreate}
              editData={editData}
              action={modalAction}
              callApi={() => fetchChargeMasterData(1, pageSize, defaultFilterData)}
              columnData={columnData}
            />
          )}

          {isModalOpenFilter && (
            <RateCardFilterModal
              show={isModalOpenFilter}
              handleClose={closeModalFilter}
              filterData={filterData}
              setFilterData={setFilterData}
              handleReset={handleReset}
              applyFilters={applyFilters}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default RateCardMaster;

